import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut, Check, ArrowRightFromLine, ArrowLeftToLine, RefreshCw, Search, Users } from 'lucide-react';
import VersionBadge from '../components/VersionBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_PILL = {
  visit: 'bg-blue-100 text-blue-700',
  break: 'bg-orange-100 text-orange-700',
  non_plan: 'bg-red-100 text-red-700'
};

export default function GateDashboard() {
  const [pendingOut, setPendingOut] = useState([]);
  const [pendingIn, setPendingIn] = useState([]);
  const [visitorPendingIn, setVisitorPendingIn] = useState([]);
  const [visitorPendingOut, setVisitorPendingOut] = useState([]);
  const [code, setCode] = useState('');
  const [visitorCode, setVisitorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [lastInResult, setLastInResult] = useState(null);
  const [lastVisitorResult, setLastVisitorResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(u);
    fetchAll();
    const t = setInterval(fetchAll, 8000);
    return () => clearInterval(t);
  }, []);

  const fetchAll = async () => {
    fetchPending();
    fetchVisitorPending();
  };

  const fetchPending = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/gate/pending`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      setPendingOut(d.pending_out || []);
      setPendingIn(d.pending_in || []);
    } catch (e) { /* silent */ }
  };

  const fetchVisitorPending = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/gate/visitor-pending`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      setVisitorPendingIn(d.pending_in || []);
      setVisitorPendingOut(d.pending_out || []);
    } catch (e) { /* silent */ }
  };

  const visitorAction = async (mode) => {
    if (!visitorCode.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/gate/visitors/approve-${mode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_code: visitorCode.trim().toUpperCase() })
      });
      const d = await r.json();
      if (r.ok) {
        setLastVisitorResult({ ok: true, mode, name: d.visit?.visitor_name, code: d.visit?.entry_code });
        setVisitorCode('');
        fetchVisitorPending();
      } else {
        setLastVisitorResult({ ok: false, error: d.detail });
      }
    } finally { setLoading(false); }
  };

  const approveOut = async (movement_id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/gate/approve-out`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ movement_id })
      });
      const d = await r.json();
      if (r.ok) fetchPending();
      else alert(d.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const approveIn = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/gate/approve-in`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ short_code: code.trim().toUpperCase() })
      });
      const d = await r.json();
      if (r.ok) {
        setLastInResult({ ok: true, staff: d.movement?.staff_name, code: d.movement?.short_code });
        setCode('');
        fetchPending();
      } else {
        setLastInResult({ ok: false, error: d.detail });
      }
    } finally { setLoading(false); }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 sticky top-0 z-30">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="text-yellow-400" size={28} />
            <div>
              <p className="font-bold flex items-center gap-2">Gate Control <VersionBadge tone="dark" /></p>
              <p className="text-xs text-slate-400">{user.name} • {user.mobile}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={fetchAll} className="p-2 hover:bg-slate-700 rounded-lg" data-testid="gate-refresh-btn">
              <RefreshCw size={18} />
            </button>
            <button onClick={handleLogout} className="p-2 text-red-400 hover:bg-red-900/30 rounded-lg" data-testid="gate-logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
        {/* Pending OUT queue */}
        <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700" data-testid="pending-out-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <ArrowRightFromLine className="text-orange-400" size={22} />
              <h2 className="text-xl font-bold">Going Out ({pendingOut.length})</h2>
            </div>
          </div>

          {pendingOut.length === 0 && (
            <div className="text-center py-12 text-slate-500 text-sm">No one waiting to leave</div>
          )}

          <div className="space-y-3">
            {pendingOut.map(m => (
              <div key={m.id} className="bg-slate-900 rounded-xl p-4 border border-slate-700 hover:border-orange-400 transition-colors" data-testid={`pending-out-${m.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-bold">{m.staff_name}</h3>
                    <p className="text-xs text-slate-400">{m.staff_emp_id} • {m.staff_mobile}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full font-medium capitalize ${TYPE_PILL[m.type] || 'bg-gray-100 text-gray-700'}`}>
                    {m.type}{m.ref_meta?.break_type ? `: ${m.ref_meta.break_type}` : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div>
                    {m.entry_otp && (
                      <p className="text-[1.8rem] font-bold tracking-[0.3em] tabular-nums text-emerald-300 mb-1 leading-none" data-testid={`otp-${m.id}`}>
                        {m.entry_otp}
                      </p>
                    )}
                    <p className="text-sm font-mono tracking-wider text-yellow-300/70" data-testid={`code-${m.id}`}>{m.short_code}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {m.ref_meta?.supplier_name && `→ ${m.ref_meta.supplier_name}`}
                      {m.ref_meta?.purpose && ` • ${m.ref_meta.purpose}`}
                      {' • requested at '} {fmtTime(m.out_request_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => approveOut(m.id)}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 disabled:opacity-50 px-5 py-3 rounded-xl font-bold flex items-center space-x-2"
                    data-testid={`approve-out-${m.id}`}
                  >
                    <Check size={20} />
                    <span>Allow OUT</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Approve IN by code */}
        <div className="space-y-4">
          <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700" data-testid="approve-in-section">
            <div className="flex items-center space-x-2 mb-4">
              <ArrowLeftToLine className="text-green-400" size={22} />
              <h2 className="text-xl font-bold">Coming In — Enter Code</h2>
            </div>
            <form onSubmit={approveIn} className="space-y-3">
              <div className="flex space-x-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="VIS-XXXXX or BRK-XXXXX"
                    className="w-full pl-10 pr-3 py-4 bg-slate-900 border border-slate-700 rounded-xl text-2xl font-mono tracking-widest text-yellow-300 focus:outline-none focus:border-yellow-400"
                    data-testid="gate-code-input"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !code.trim()}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 px-6 py-4 rounded-xl font-bold flex items-center space-x-2"
                  data-testid="gate-approve-in-btn"
                >
                  <Check size={20} /><span>Allow IN</span>
                </button>
              </div>
              {lastInResult && (
                <div className={`p-3 rounded-lg text-sm ${lastInResult.ok ? 'bg-green-900/40 border border-green-700 text-green-200' : 'bg-red-900/40 border border-red-700 text-red-200'}`} data-testid="gate-in-result">
                  {lastInResult.ok ? (
                    <>✓ <strong>{lastInResult.staff}</strong> allowed in (code {lastInResult.code})</>
                  ) : (
                    <>✗ {lastInResult.error}</>
                  )}
                </div>
              )}
            </form>
          </div>

          {/* Pending IN list (showing codes that have been requested) */}
          <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700" data-testid="pending-in-section">
            <h3 className="font-semibold mb-3 text-slate-300">Awaiting Entry ({pendingIn.length})</h3>
            {pendingIn.length === 0 && (
              <p className="text-center py-8 text-slate-500 text-sm">No one is returning right now</p>
            )}
            <div className="space-y-2">
              {pendingIn.map(m => (
                <div key={m.id} className="bg-slate-900 rounded-lg p-3 flex items-center justify-between" data-testid={`pending-in-${m.id}`}>
                  <div>
                    <p className="font-bold">{m.staff_name}</p>
                    <p className="text-xs text-slate-400 capitalize">{m.type}{m.ref_meta?.break_type ? `: ${m.ref_meta.break_type}` : ''} • returned {fmtTime(m.in_request_at)}</p>
                  </div>
                  <div className="text-right">
                    {m.exit_otp && (
                      <p className="text-3xl font-bold tracking-[0.25em] tabular-nums text-emerald-300" data-testid={`otp-in-${m.id}`}>
                        {m.exit_otp}
                      </p>
                    )}
                    <span className="text-xs font-mono tracking-wider text-yellow-300/70">{m.short_code}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* VISITORS SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-4 pb-8">
        {/* Visitor pending IN cards */}
        <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700" data-testid="visitor-pending-out-section">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="text-purple-400" size={22} />
            <h2 className="text-xl font-bold">Visitors Going IN ({visitorPendingIn.length})</h2>
          </div>
          {visitorPendingIn.length === 0 && (
            <div className="text-center py-12 text-slate-500 text-sm">No visitors waiting</div>
          )}
          <div className="space-y-3">
            {visitorPendingIn.map(v => (
              <div key={v.id} className="bg-slate-900 rounded-xl p-4 border border-slate-700 hover:border-purple-400" data-testid={`visitor-pending-${v.id}`}>
                <div className="flex items-start space-x-3">
                  {v.visitor_photo && (
                    <img src={v.visitor_photo} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-slate-600" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-bold">{v.visitor_name}</h3>
                    <p className="text-xs text-slate-400">{v.visitor_company} • {v.visitor_mobile}</p>
                    <p className="text-xs text-slate-300 mt-1">→ Meeting <strong>{v.staff_name}</strong></p>
                    {v.purpose && <p className="text-xs text-slate-400">Purpose: {v.purpose}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                  <p className="text-xl font-mono font-bold tracking-wider text-yellow-300">{v.entry_code}</p>
                  <span className="text-xs text-slate-500">requested {fmtTime(v.in_request_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Visitor approve by code + going out queue */}
        <div className="space-y-4">
          <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700" data-testid="visitor-approve-section">
            <div className="flex items-center space-x-2 mb-4">
              <Users className="text-purple-400" size={22} />
              <h2 className="text-xl font-bold">Visitor — Enter Code</h2>
            </div>
            <p className="text-xs text-slate-400 mb-3">Same code is used for both IN and OUT.</p>
            <div className="flex space-x-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  value={visitorCode}
                  onChange={(e) => setVisitorCode(e.target.value.toUpperCase())}
                  placeholder="VST-XXXXX"
                  className="w-full pl-10 pr-3 py-3 bg-slate-900 border border-slate-700 rounded-xl text-xl font-mono tracking-widest text-yellow-300 focus:outline-none focus:border-yellow-400"
                  data-testid="visitor-code-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                onClick={() => visitorAction('in')}
                disabled={loading || !visitorCode.trim()}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-50 px-4 py-3 rounded-xl font-bold flex items-center justify-center space-x-2"
                data-testid="visitor-allow-in-btn"
              >
                <ArrowLeftToLine size={16} /><span>Allow IN</span>
              </button>
              <button
                onClick={() => visitorAction('out')}
                disabled={loading || !visitorCode.trim()}
                className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 px-4 py-3 rounded-xl font-bold flex items-center justify-center space-x-2"
                data-testid="visitor-allow-out-btn"
              >
                <ArrowRightFromLine size={16} /><span>Allow OUT</span>
              </button>
            </div>
            {lastVisitorResult && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${lastVisitorResult.ok ? 'bg-green-900/40 border border-green-700 text-green-200' : 'bg-red-900/40 border border-red-700 text-red-200'}`} data-testid="visitor-result">
                {lastVisitorResult.ok ? (
                  <>✓ <strong>{lastVisitorResult.name}</strong> allowed {lastVisitorResult.mode === 'in' ? 'IN' : 'OUT'} ({lastVisitorResult.code})</>
                ) : (
                  <>✗ {lastVisitorResult.error}</>
                )}
              </div>
            )}
          </div>

          <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700" data-testid="visitor-pending-out-list">
            <h3 className="font-semibold mb-3 text-slate-300">Visitors Going OUT ({visitorPendingOut.length})</h3>
            {visitorPendingOut.length === 0 && (
              <p className="text-center py-8 text-slate-500 text-sm">No visitor waiting to leave</p>
            )}
            <div className="space-y-2">
              {visitorPendingOut.map(v => (
                <div key={v.id} className="bg-slate-900 rounded-lg p-3 flex items-center justify-between" data-testid={`visitor-out-${v.id}`}>
                  <div>
                    <p className="font-bold">{v.visitor_name}</p>
                    <p className="text-xs text-slate-400">{v.visitor_company} • met {v.staff_name}</p>
                  </div>
                  <span className="text-yellow-300 font-mono font-bold tracking-wider">{v.entry_code}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
